<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <div>
        <AccountsList
            v-if="view === 'Accounts'"
            :is-loading="isLoading"
            :publish-meta-accounts-status="publishMetaAccountsStatus"
            @showVariantsSelect="showVariantsSelect"
            @fetchUpdatedCampaignInfo="getCampaignInfo"
        />

        <VariantsSelect
            v-else
            :platform-account-name="selectedPlatformAccountName"
            :rule-name="selectedRuleName"
            :is-loading-campaign-data="isLoadingCampaignData"
            :campaign-loading-error="campaignLoadingError"
            @fetchCampaignData="fetchCampaignData"
            @close="closeVariationsSelect"
        />

        <AlertModal
            v-model="isDiscardChangesModalVisible"
            header="Leaving the page"
            title="Do you really want to leave?"
            content="Unsaved changes will be lost. This cannot be undone."
            :warning="true"
            :on-ok="handleDiscardChanges"
        />
    </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRoute, onBeforeRouteLeave } from "vue-router";
import { useStore } from "vuex";

import VariantsSelect from "@/Connect/components/VariantsSelect/VariantsSelect.vue";
import AccountsList from "@/Connect/components/Accounts/PlatformAccountsList.vue";
import AlertModal from "@/components/Modal/Alert.vue";

import { apolloClient } from "@/apollo";
import CampaignEditorQuery from "@/apollo/queries/v2/CampaignEditor.gql";
import ComputedDeliverableComponentsQuery from "@/apollo/queries/v2/ComputedDeliverableComponents.gql";

import { CampaignAction } from "@/store/modules/campaign";

import { useConnectStore } from "@/Connect/store";

import * as Auth from "@/services/Auth";
import { getAccounts } from "@/utils/CreativeIntelligenceApi/connect";
import { snackbar } from "@/utils";
import { getInvalidMetaEditables } from "@/Connect/utils/getInvalidMetaEditables";
import { getInvalidTikTokEditables } from "@/Connect/utils/getInvalidTikTokEditables";
import { PlatformTypeOption } from "@/enums/platforms";
import { OpenDcCampaignType } from "hoxton-shared/enums/opendc";

/** SETUP */
const store = useStore();
const connectStore = useConnectStore();

const route = useRoute();
const clientId = route.params.clientId as string;
const campaignId = route.params.campaignId as string;

const hasScope = computed(() => Auth.hasScope({ clientId, campaignId }));

/** LOCAL STATE */
const view = ref<"Accounts" | "VariantsSelect">("Accounts");

const selectedPlatformAccountName = ref("");
const selectedRuleName = ref("");

const isLoadingCampaignInfo = ref(false);

const isLoadingCampaignData = ref(false);
const campaignLoadingError = ref(false);

const isLoadingDeliverableComponents = ref(false);

const isDiscardChangesModalVisible = ref(false);

const nextRoute = ref();

const editablesWithOverwrites = ref([]);
const editableGroupOptions = computed(() => connectStore.editableGroupOptions);
const visibleAccounts = computed(() => connectStore.visibleAccounts);
const publishMetaAccountsStatus = computed(() => {
    const editableOptions =
        editableGroupOptions.value[PlatformTypeOption.facebookPlatform]?.editableOptions ??
        editableGroupOptions.value[PlatformTypeOption.platformMetadata]?.editableOptions ??
        [];

    if (!editableOptions?.length) {
        return {
            canPublish: false,
            message:
                "This campaign can't be published: the Meta platform account details are not properly configured in Assemble.",
            extraInfo: []
        };
    }
    const visibleAccountsEditableGroupsIds = Array.from(
        new Set(
            (visibleAccounts?.value ?? []).flatMap(account =>
                (account.rules ?? [])
                    .filter(rule => rule.status === "active")
                    .flatMap(rule => rule.variantsFilters?.editableGroupValueIds ?? [])
            )
        )
    );

    const filteredOptions = editableOptions.filter(option => visibleAccountsEditableGroupsIds.includes(option.value));
    if (!filteredOptions) {
        return {
            canPublish: false,
            message:
                "This campaign can't be published: the Meta platform account details are not properly configured in Assemble.",
            extraInfo: []
        };
    }
    const campaignType = store.state.campaign.opendcPlatformSettings?.campaignType;
    const isTikTok = campaignType === OpenDcCampaignType.tiktok;

    const allErrors = filteredOptions.flatMap(option =>
        isTikTok
            ? getInvalidTikTokEditables(editablesWithOverwrites.value, option)
            : getInvalidMetaEditables(editablesWithOverwrites.value, option)
    );

    if (allErrors?.length) {
        return {
            canPublish: false,
            message: isTikTok
                ? "This campaign can't be published: the TikTok platform account details are not properly configured in Assemble."
                : "This campaign can't be published: the Meta platform account details are not properly configured in Assemble.",
            extraInfo: allErrors
        };
    }

    if (!connectStore.hasActiveRules) {
        return {
            canPublish: false,
            message: "This campaign can't be published: the campaign has no active rules.",
            extraInfo: []
        };
    }

    return {
        canPublish: true,
        message: "",
        extraInfo: []
    };
});

/** COMPUTED */
const isLoading = computed(
    () => isLoadingCampaignInfo.value || isLoadingCampaignData.value || isLoadingDeliverableComponents.value
);

/** EVENTS */
function showVariantsSelect({ platformAccountName, ruleName }: { platformAccountName: string; ruleName: string }) {
    view.value = "VariantsSelect";

    selectedPlatformAccountName.value = platformAccountName;
    selectedRuleName.value = ruleName;
}

function closeVariationsSelect() {
    view.value = "Accounts";

    selectedPlatformAccountName.value = "";
    selectedRuleName.value = "";
}

onBeforeRouteLeave((to, from, next) => {
    if (connectStore.hasAccountChanges) {
        isDiscardChangesModalVisible.value = true;
        nextRoute.value = next;
    } else {
        next();
    }
});

function handleDiscardChanges() {
    isDiscardChangesModalVisible.value = false;

    nextRoute.value();
    nextRoute.value = null;
}

async function getCampaignInfo() {
    try {
        isLoadingCampaignInfo.value = true;

        const { data } = await getAccounts({ campaignId });

        connectStore.setAccounts(data.accountsRules);
        connectStore.latestVersionId = data.latestVersionId;
    } catch (e) {
        snackbar.error((e as Error)?.message || "There was an error please try again.");
    }

    isLoadingCampaignInfo.value = false;
}

async function fetchCampaignData() {
    isLoadingCampaignData.value = true;
    campaignLoadingError.value = false;

    try {
        const { data } = await apolloClient.query({
            fetchPolicy: "network-only",
            query: CampaignEditorQuery,
            variables: { campaignId }
        });

        store.dispatch(CampaignAction.SetCampaign, data.campaign);
        store.dispatch(CampaignAction.SetMasterTemplates, data.masterTemplatesByCampaign);
        store.dispatch(CampaignAction.SetGroups, data.editableGroupsByCampaign);

        editablesWithOverwrites.value = data.editablesWithOverwritesByCampaign;
    } catch (error) {
        console.error(error);
        campaignLoadingError.value = true;
    }

    isLoadingCampaignData.value = false;
}

interface ComputedDeliverableComponentsQueryResponse {
    computedDeliverableComponents: {
        deliverableCount: number;
        languages: { label: string; value: string }[];
        masterTemplates: { height: number; width: number; _id: string }[];
        editableGroupValues: { editableGroupName: string; value: string; _id: string }[];
    };
}

async function fetchComputedDeliverableComponents() {
    isLoadingDeliverableComponents.value = true;

    try {
        const {
            data: {
                computedDeliverableComponents: { deliverableCount, editableGroupValues, languages, masterTemplates }
            }
        } = await apolloClient.query<ComputedDeliverableComponentsQueryResponse>({
            fetchPolicy: "network-only",
            query: ComputedDeliverableComponentsQuery,
            variables: { campaignId }
        });

        connectStore.totalVariations = deliverableCount;
        connectStore.languageOptions = languages;
        connectStore.setSizeOptionsFromMasterTemplates(masterTemplates);
        connectStore.setEditableGroupOptionsFromGroupValues(editableGroupValues);
    } catch (error) {
        console.error(error);
    }

    isLoadingDeliverableComponents.value = false;
}

/** INIT */
onMounted(async () => {
    getCampaignInfo();
});

watch(
    hasScope,
    newValue => {
        if (newValue) {
            fetchCampaignData();
            fetchComputedDeliverableComponents();
        }
    },
    { immediate: true }
);
</script>

<style lang="scss">
/* disables the weird way fixed headers are handled normally */
.app-wrapper.wpp-theme--connect .campaign-content .main-content {
    padding-top: 0 !important;
}
</style>
